import type { MutationArgs, MutationName, SyncedTableName } from "@/lib/contract";

import { appendPosition, byPosition } from "./ordering";
import { columnsFor, defaultColumn } from "./selectors";
import type { EntityMap, PendingMutation, SyncSession, Tables } from "./types";

/**
 * Optimistic updates: what each mutation does to the local replica *before*
 * the server has seen it.
 *
 * These are not the source of truth and do not need to be perfect. Every one
 * of them is replayed on top of server state until the server's own version
 * of the change arrives, at which point it is discarded. So a reducer that
 * guesses slightly wrong — an optimistic task number that someone else took a
 * moment earlier — corrects itself within a round trip. What they must do is
 * get the *visible* result right in the same frame as the click, because that
 * is the entire point of a local-first client.
 *
 * Reducers are pure and copy only what they change: unchanged rows keep their
 * identity, so a card whose row did not change does not re-render.
 */

interface Context {
  session: SyncSession;
  now: string;
}

function syncFields(id: string, ctx: Context) {
  return {
    id,
    workspaceId: ctx.session.workspaceId,
    version: 0,
    createdAt: ctx.now,
    updatedAt: ctx.now,
    deletedAt: null,
  };
}

function put<T extends SyncedTableName>(tables: Tables, table: T, row: EntityMap[T]): Tables {
  return { ...tables, [table]: { ...tables[table], [row.id]: row } };
}

function patch<T extends SyncedTableName>(
  tables: Tables,
  table: T,
  id: string,
  fields: Partial<EntityMap[T]>,
  ctx: Context,
): Tables {
  const row = tables[table][id];
  if (!row) return tables;
  return put(tables, table, { ...row, ...fields, updatedAt: ctx.now });
}

function remove<T extends SyncedTableName>(
  tables: Tables,
  table: T,
  predicate: (row: EntityMap[T]) => boolean,
): Tables {
  const rows = tables[table];
  const kept: Record<string, EntityMap[T]> = {};
  let changed = false;
  for (const [id, row] of Object.entries(rows)) {
    if (predicate(row)) changed = true;
    else kept[id] = row;
  }
  return changed ? { ...tables, [table]: kept } : tables;
}

/** Copies only the keys actually present, so an absent field stays unchanged. */
function present<A extends object>(args: A, keys: readonly (keyof A)[]): Partial<A> {
  const out: Partial<A> = {};
  for (const key of keys) {
    if (key in args) out[key] = args[key];
  }
  return out;
}

type Reducers = {
  [N in MutationName]: (tables: Tables, args: MutationArgs<N>, ctx: Context) => Tables;
};

const reducers: Reducers = {
  // ------------------------------------------------------------------ tasks
  createTask(tables, args, ctx) {
    if (tables.tasks[args.id] || !tables.projects[args.projectId]) return tables;

    const stateId = args.stateId ?? defaultColumn(tables.workflowStates, args.projectId)?.id;
    if (!stateId) return tables;

    const inColumn = Object.values(tables.tasks)
      .filter((task) => task.projectId === args.projectId && task.stateId === stateId)
      .sort(byPosition);
    // A guess; the server allocates the real number and its row replaces this one.
    const number =
      Object.values(tables.tasks).reduce(
        (max, task) => (task.projectId === args.projectId ? Math.max(max, task.number) : max),
        0,
      ) + 1;
    const stateType = tables.workflowStates[stateId]?.type;

    return put(tables, "tasks", {
      ...syncFields(args.id, ctx),
      projectId: args.projectId,
      number,
      title: args.title,
      description: args.description ?? null,
      stateId,
      assigneeId: args.assigneeId ?? null,
      creatorId: ctx.session.userId,
      priority: args.priority ?? "none",
      estimate: args.estimate ?? null,
      position: args.position ?? appendPosition(inColumn),
      dueDate: args.dueDate ?? null,
      startedAt: stateType === "started" ? ctx.now : null,
      completedAt: stateType === "completed" ? ctx.now : null,
      parentId: args.parentId ?? null,
      sourceSuggestionId: args.sourceSuggestionId ?? null,
    });
  },

  updateTask(tables, args, ctx) {
    const fields = present(args, [
      "title",
      "description",
      "assigneeId",
      "priority",
      "estimate",
      "dueDate",
    ] as const);
    return patch(tables, "tasks", args.id, fields as Partial<EntityMap["tasks"]>, ctx);
  },

  moveTask(tables, args, ctx) {
    const task = tables.tasks[args.id];
    const target = tables.workflowStates[args.stateId];
    if (!task || !target) return tables;
    return patch(
      tables,
      "tasks",
      args.id,
      {
        stateId: args.stateId,
        position: args.position,
        ...(args.projectId ? { projectId: args.projectId } : {}),
        // First transition only, exactly as the server stamps them.
        ...(target.type === "started" && task.startedAt === null ? { startedAt: ctx.now } : {}),
        ...(target.type === "completed" && task.completedAt === null
          ? { completedAt: ctx.now }
          : {}),
      },
      ctx,
    );
  },

  deleteTask(tables, args) {
    return remove(tables, "tasks", (task) => task.id === args.id);
  },

  // Nothing to show until the server returns the row; the replica no longer
  // holds deleted tasks.
  restoreTask(tables) {
    return tables;
  },

  // --------------------------------------------------------------- comments
  createComment(tables, args, ctx) {
    if (tables.comments[args.id]) return tables;
    return put(tables, "comments", {
      ...syncFields(args.id, ctx),
      taskId: args.taskId,
      authorId: ctx.session.userId,
      body: args.body,
      parentId: args.parentId ?? null,
      slackChannelId: null,
      slackMessageTs: null,
    });
  },

  updateComment(tables, args, ctx) {
    return patch(tables, "comments", args.id, { body: args.body }, ctx);
  },

  deleteComment(tables, args) {
    return remove(tables, "comments", (comment) => comment.id === args.id);
  },

  // ----------------------------------------------------------------- labels
  createLabel(tables, args, ctx) {
    if (tables.labels[args.id]) return tables;
    return put(tables, "labels", {
      ...syncFields(args.id, ctx),
      name: args.name,
      color: args.color.toLowerCase(),
    });
  },

  updateLabel(tables, args, ctx) {
    const fields = present(args, ["name", "color"] as const);
    if (fields.color) fields.color = fields.color.toLowerCase();
    return patch(tables, "labels", args.id, fields as Partial<EntityMap["labels"]>, ctx);
  },

  deleteLabel(tables, args) {
    const withoutLinks = remove(tables, "taskLabels", (link) => link.labelId === args.id);
    return remove(withoutLinks, "labels", (label) => label.id === args.id);
  },

  addTaskLabel(tables, args, ctx) {
    // The server revives an old (task, label) row rather than adding a second
    // one, so if a live link already exists there is nothing to add.
    const exists = Object.values(tables.taskLabels).some(
      (link) => link.taskId === args.taskId && link.labelId === args.labelId,
    );
    if (exists) return tables;
    return put(tables, "taskLabels", {
      ...syncFields(args.id, ctx),
      taskId: args.taskId,
      labelId: args.labelId,
    });
  },

  removeTaskLabel(tables, args) {
    return remove(
      tables,
      "taskLabels",
      (link) => link.taskId === args.taskId && link.labelId === args.labelId,
    );
  },

  // --------------------------------------------------------------- projects
  createProject(tables, args, ctx) {
    if (tables.projects[args.id]) return tables;
    const projects = Object.values(tables.projects).sort(byPosition);
    let next = put(tables, "projects", {
      ...syncFields(args.id, ctx),
      name: args.name,
      key: args.key,
      description: args.description ?? null,
      status: "active",
      leadId: args.leadId ?? null,
      startDate: args.startDate ?? null,
      targetDate: args.targetDate ?? null,
      position: args.position ?? appendPosition(projects),
      isPrivate: args.isPrivate ?? false,
    });
    if (args.isPrivate) {
      // The creator is the first member, as on the server.
      next = put(next, "projectMembers", {
        ...syncFields(`${args.id}:${ctx.session.userId}`, ctx),
        projectId: args.id,
        userId: ctx.session.userId,
      });
    }
    return next;
  },

  updateProject(tables, args, ctx) {
    const fields = present(args, [
      "name",
      "description",
      "status",
      "leadId",
      "startDate",
      "targetDate",
      "position",
    ] as const);
    return patch(tables, "projects", args.id, fields as Partial<EntityMap["projects"]>, ctx);
  },

  deleteProject(tables, args) {
    const withoutTasks = remove(tables, "tasks", (task) => task.projectId === args.id);
    return remove(withoutTasks, "projects", (project) => project.id === args.id);
  },

  addProjectMember(tables, args, ctx) {
    const exists = Object.values(tables.projectMembers).some(
      (member) => member.projectId === args.projectId && member.userId === args.userId,
    );
    if (exists) return tables;
    return put(tables, "projectMembers", {
      ...syncFields(args.id, ctx),
      projectId: args.projectId,
      userId: args.userId,
    });
  },

  removeProjectMember(tables, args) {
    return remove(
      tables,
      "projectMembers",
      (member) => member.projectId === args.projectId && member.userId === args.userId,
    );
  },

  // ---------------------------------------------------------------- columns
  createWorkflowState(tables, args, ctx) {
    if (tables.workflowStates[args.id]) return tables;
    const siblings = args.projectId ? columnsFor(tables.workflowStates, args.projectId) : [];
    return put(tables, "workflowStates", {
      ...syncFields(args.id, ctx),
      projectId: args.projectId ?? null,
      name: args.name,
      type: args.type,
      color: (args.color ?? "8b8b8b").toLowerCase(),
      position: args.position ?? appendPosition(siblings),
    });
  },

  updateWorkflowState(tables, args, ctx) {
    const fields = present(args, ["name", "type", "color", "position"] as const);
    if (fields.color) fields.color = fields.color.toLowerCase();
    return patch(
      tables,
      "workflowStates",
      args.id,
      fields as Partial<EntityMap["workflowStates"]>,
      ctx,
    );
  },

  deleteWorkflowState(tables, args, ctx) {
    // Cards move to the end of the destination column in their current order.
    const moving = Object.values(tables.tasks)
      .filter((task) => task.stateId === args.id)
      .sort(byPosition);
    let next = tables;
    for (const task of moving) {
      const destination = Object.values(next.tasks)
        .filter((t) => t.stateId === args.moveTasksToStateId && t.projectId === task.projectId)
        .sort(byPosition);
      next = patch(
        next,
        "tasks",
        task.id,
        { stateId: args.moveTasksToStateId, position: appendPosition(destination) },
        ctx,
      );
    }
    return remove(next, "workflowStates", (state) => state.id === args.id);
  },

  // ---------------------------------------------------------- notifications
  // The server refuses all three for anyone but an admin, and for yourself;
  // the screens only offer them to an admin acting on someone else, so the
  // optimistic result is the one that sticks.
  updateUserRole(tables, args, ctx) {
    return patch(tables, "users", args.id, { role: args.role }, ctx);
  },

  deactivateUser(tables, args, ctx) {
    return patch(tables, "users", args.id, { status: "deactivated" }, ctx);
  },

  reactivateUser(tables, args, ctx) {
    // The server decides between active and invited from whether they ever set
    // up a sign-in; the client cannot see that, and "active" is the usual case.
    return patch(tables, "users", args.id, { status: "active" }, ctx);
  },

  markNotificationRead(tables, args, ctx) {
    const row = tables.notifications[args.id];
    if (row?.userId !== ctx.session.userId || row.readAt !== null) return tables;
    return patch(tables, "notifications", args.id, { readAt: ctx.now }, ctx);
  },

  markAllNotificationsRead(tables, args, ctx) {
    let next = tables;
    for (const row of Object.values(tables.notifications)) {
      if (
        row.userId === ctx.session.userId &&
        row.readAt === null &&
        row.version <= args.upToVersion
      ) {
        next = patch(next, "notifications", row.id, { readAt: ctx.now }, ctx);
      }
    }
    return next;
  },

  setNotificationPreference(tables, args, ctx) {
    const existing = Object.values(tables.notificationPreferences).find(
      (pref) => pref.userId === ctx.session.userId && pref.type === args.type,
    );
    if (existing) {
      return patch(
        tables,
        "notificationPreferences",
        existing.id,
        {
          ...(args.inApp !== undefined ? { inApp: args.inApp } : {}),
          ...(args.slack !== undefined ? { slack: args.slack } : {}),
        },
        ctx,
      );
    }
    return put(tables, "notificationPreferences", {
      ...syncFields(args.id, ctx),
      userId: ctx.session.userId,
      type: args.type,
      inApp: args.inApp ?? true,
      slack: args.slack ?? true,
    });
  },

  setTaskSubscription(tables, args, ctx) {
    const existing = Object.values(tables.taskSubscriptions).find(
      (sub) => sub.taskId === args.taskId && sub.userId === ctx.session.userId,
    );
    if (existing) {
      return patch(tables, "taskSubscriptions", existing.id, { subscribed: args.subscribed }, ctx);
    }
    return put(tables, "taskSubscriptions", {
      ...syncFields(args.id, ctx),
      taskId: args.taskId,
      userId: ctx.session.userId,
      subscribed: args.subscribed,
    });
  },
};

/** One pending mutation, applied to a replica. */
export function applyLocal(
  tables: Tables,
  mutation: PendingMutation,
  session: SyncSession,
): Tables {
  const reducer = reducers[mutation.name] as (
    tables: Tables,
    args: unknown,
    ctx: Context,
  ) => Tables;
  return reducer(tables, mutation.args, { session, now: mutation.createdAt });
}

/** Server state with every pending mutation replayed on top, oldest first. */
export function rebase(
  base: Tables,
  pending: readonly PendingMutation[],
  session: SyncSession | null,
): Tables {
  if (!session || pending.length === 0) return base;
  return pending.reduce((tables, mutation) => applyLocal(tables, mutation, session), base);
}
